import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const PricingPage = () => {
  const plans = [
    {
      name: 'Starter Sprint',
      duration: '2 weeks',
      //price: '$6,900',
      subtitle: 'Fixed scope • Cross‑functional pod',
      description: 'Validate an idea or ship a focused feature fast.',
      features: [
        'Discovery workshop and plan',
        'UX flows, wireframes, clickable prototype',
        'Dev spike and technical approach'
      ],
      buttonText: 'Request proposal',
      highlighted: false
    },
    {
      name: 'Growth',
      duration: 'Monthly',
      //price: '$18,000',
      subtitle: 'Per month • Cancel anytime',
      description: 'Add capacity with a steady cadence of delivery.',
      features: [
        'Cross‑functional squad (PM, Eng, QA, Design)',
        'CI/CD, monitoring, and weekly releases',
        'Backlog shaping and quarterly roadmap'
      ],
      buttonText: 'Request proposal',
      highlighted: false
    },
    {
      name: 'Dedicated Team',
      duration: 'Best Value',
      price: 'Custom',
      subtitle: 'Tailored to your goals & velocity',
      description: 'A full product team embedded with yours.',
      features: [
        'Senior engineers, PM, Design, DevOps, QA',
        'SLAs, SLOs, and on‑call coverage',
        'Security & compliance support'
      ],
      buttonText: 'Talk to us',
      highlighted: true
    }
  ];

  return (
    <div className="pt-24">
      <SEO 
        title="Software Development Pricing in Atlanta, GA | Alpha Consulting LLC"
        description="Explore flexible software development pricing for Atlanta and Georgia businesses. Choose from starter sprints, monthly product delivery, or a dedicated team engagement."
        keywords="software development pricing Atlanta GA, custom software cost Georgia, dedicated development team Atlanta, product engineering pricing Georgia"
        url="https://thealphaconsult.com/pricing"
        image="https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
      />
      <section className="relative py-20 bg-gradient-to-b from-orange-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300/80 font-nunito">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> Plans
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-nunito font-bold">
              Flexible engagement models
            </h2>
            <p className="mt-3 text-orange-300 font-nunito">
              Start small or scale up, choose a plan that fits your timeline and budget.
            </p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`group relative rounded-2xl border p-6 hover:border-amber-500/40 transition-all ${
                  plan.highlighted 
                    ? 'border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-amber-500/5' 
                    : 'border-white/10 bg-orange-900/40'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold font-nunito">{plan.name}</h3>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                    plan.highlighted 
                      ? 'bg-amber-500/15 text-amber-300 border-amber-300/20'
                      : 'bg-amber-500/15 text-amber-300 border-amber-300/20'
                  }`}>
                    {plan.duration}
                  </span>
                </div>
                <p className={`mt-2 font-nunito ${plan.highlighted ? 'text-orange-100' : 'text-orange-300'}`}>
                  {plan.description}
                </p>
                <div className="mt-4">
                  <div className="text-3xl font-bold font-nunito">{plan.price}</div>
                  <div className={`text-sm font-nunito ${plan.highlighted ? 'text-orange-300' : 'text-orange-400'}`}>
                    {plan.subtitle}
                  </div>
                </div>
                <ul className="mt-5 space-y-2 text-sm">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className={`flex items-start gap-2 ${plan.highlighted ? 'text-orange-50' : 'text-orange-200'}`}>
                      <svg 
                        className={`w-4 h-4 mt-0.5 ${plan.highlighted ? 'text-amber-300' : 'text-emerald-400'}`} 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full transition font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 font-nunito ${
                    plan.highlighted
                      ? 'bg-amber-300 text-black hover:bg-amber-200'
                      : 'bg-orange-100 text-black hover:bg-orange-200'
                  }`}
                >
                  {plan.buttonText}
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                  </svg>
                </Link>
                <div className={`pointer-events-none absolute inset-px rounded-2xl ring-1 ring-inset transition ${
                  plan.highlighted 
                    ? 'ring-amber-400/30 group-hover:ring-amber-300/50'
                    : 'ring-white/10 group-hover:ring-amber-500/40'
                }`}></div>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-semibold font-nunito mb-6 text-center">Frequently Asked Questions</h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h4 className="font-semibold font-nunito text-amber-300 mb-2">What's included in each plan?</h4>
                <p className="text-orange-300 font-nunito text-sm">
                  Each plan is tailored to different project needs. All plans include professional engineering, 
                  design support, and quality assurance appropriate to the engagement level.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h4 className="font-semibold font-nunito text-amber-300 mb-2">Can I switch plans?</h4>
                <p className="text-orange-300 font-nunito text-sm">
                  Yes! We're flexible and can adjust your engagement as your needs evolve. 
                  You can scale up or down based on project requirements.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h4 className="font-semibold font-nunito text-amber-300 mb-2">Do you offer support after launch?</h4>
                <p className="text-orange-300 font-nunito text-sm">
                  Absolutely! We provide ongoing maintenance, monitoring, and support packages 
                  to ensure your application runs smoothly post-launch.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h4 className="font-semibold font-nunito text-amber-300 mb-2">What technologies do you work with?</h4>
                <p className="text-orange-300 font-nunito text-sm">
                  We specialize in modern web technologies including React, Node.js, Python, AWS, and more. 
                  We'll recommend the best tech stack for your specific needs.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h4 className="font-semibold font-nunito text-amber-300 mb-2">Do you offer hardware procurement services?</h4>
                <p className="text-orange-300 font-nunito text-sm">
                  Yes! We provide hardware procurement services to ensure you have the right infrastructure 
                  for your projects. We can help source, configure, and deploy hardware solutions tailored to your needs.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-orange-900/40 p-6">
                <h4 className="font-semibold font-nunito text-amber-300 mb-2">Can you help with security and compliance?</h4>
                <p className="text-orange-300 font-nunito text-sm">
                  Absolutely! We offer comprehensive security service line compliance management and policy implementation 
                  to help your organization meet industry standards and regulatory requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
